/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';

import styles from './EventBranding.style';

import messages from 'src/shared/app/nav/l10n/navigationL10n';
import ResponsivePicture from '@e180/ui/lib/component/ResponsivePicture';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { homeRoute } from 'src/shared/app/base/route/setting/routeSettings';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';

import type { Picture } from '@e180/domain/lib/base/type';

type Props = {
  variant: typeof large | typeof xlarge,
  name: string,
  logo: Picture,
  hasCustomBraindateName: boolean,
  hasSponsors: boolean,
};

const useStyles = createUseStyles(styles);

const EventBrandingDumb = ({
  variant,
  name,
  logo,
  hasCustomBraindateName,
  hasSponsors,
}: Props): React.Node => {
  let imgWidth = 80;

  if (variant === large) {
    imgWidth = 140;
  } else if (variant === xlarge) {
    imgWidth = 300;
  }

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const { braindatePlatform: platform } = useEventBraindateName();
  const [homePath] = useRoutes(homeRoute);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ imgWidth });

  const rootClasses = classNames(classes.root, {
    [classes['root-has-sponsors']]: hasSponsors,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const textElt = !hasCustomBraindateName && (
    <span
      className={classes.text}
      dangerouslySetInnerHTML={{
        __html: intl
          .formatMessage(messages.eventBranding, { platform })
          .replace(platform, `<span class="${classes.platform}">$&</span>`),
      }}
    ></span>
  );

  return (
    <Link className={rootClasses} to={homePath}>
      {textElt}
      <ResponsivePicture
        layoutClass={classes.logo}
        picture={logo}
        alt={name}
        displayWidth={imgWidth}
      ></ResponsivePicture>
    </Link>
  );
};

export const large = Symbol();
export const xlarge = Symbol();

export default compose(React.memo)(EventBrandingDumb);
